import { useMemo, useState } from "react";
import "./SandboxDemo.css";
import SandboxLeft from "./SandboxLeft";
import SandboxRight from "./SandboxRight";

function SandboxDemo() {
  const [seasons, setSeasons] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [games, setGames] = useState([]);
  const [playerGameStats, setPlayerGameStats] = useState([]);
  const [leftView, setLeftView] = useState("seasons");
  const [activeSeasonId, setActiveSeasonId] = useState(null);
  const [activeLeagueId, setActiveLeagueId] = useState(null);
  const [activeTeamId, setActiveTeamId] = useState(null);
  const [activeGameId, setActiveGameId] = useState(null);
  const [activePreviewTab, setActivePreviewTab] = useState("standings");
  const [selectedLeagueForStandings, setSelectedLeagueForStandings] = useState("all");
  const [playerSearch, setPlayerSearch] = useState("");
  const [selectedLeagueForPlayers, setSelectedLeagueForPlayers] = useState("");
  const [selectedTeamForPlayers, setSelectedTeamForPlayers] = useState("");
  const [selectedLeagueForSchedule, setSelectedLeagueForSchedule] = useState("all");
  const [selectedTeamPreviewId, setSelectedTeamPreviewId] = useState(null);
  const [selectedGamePreviewId, setSelectedGamePreviewId] = useState(null);

  const activeSeason = useMemo(
    () => seasons.find((item) => item.id === activeSeasonId) || null,
    [seasons, activeSeasonId]
  );

  const activeLeague = useMemo(
    () => leagues.find((item) => item.id === activeLeagueId) || null,
    [leagues, activeLeagueId]
  );

  const activeTeam = useMemo(
    () => teams.find((item) => item.id === activeTeamId) || null,
    [teams, activeTeamId]
  );

  const activeGame = useMemo(
    () => games.find((item) => item.id === activeGameId) || null,
    [games, activeGameId]
  );

  const leaguesForActiveSeason = useMemo(() => {
    return leagues.filter((item) => item.seasonId === activeSeasonId);
  }, [leagues, activeSeasonId]);

  const teamsForActiveLeague = useMemo(() => {
    return teams.filter((item) => item.leagueId === activeLeagueId);
  }, [teams, activeLeagueId]);

  const playersForActiveTeam = useMemo(() => {
    return players.filter((item) => item.teamId === activeTeamId);
  }, [players, activeTeamId]);

  const gamesForActiveLeague = useMemo(() => {
    return games.filter((item) => item.leagueId === activeLeagueId);
  }, [games, activeLeagueId]);

  const teamsForSelectedPlayerLeague = useMemo(() => {
    if (!selectedLeagueForPlayers) return teams;

    return teams.filter(
      (item) => String(item.leagueId) === String(selectedLeagueForPlayers)
    );
  }, [teams, selectedLeagueForPlayers]);

  const standingsTeams = useMemo(() => {
    if (selectedLeagueForStandings === "all") return teams;

    return teams.filter(
      (item) => String(item.leagueId) === String(selectedLeagueForStandings)
    );
  }, [teams, selectedLeagueForStandings]);

  const scheduleGames = useMemo(() => {
    if (selectedLeagueForSchedule === "all") return games;

    return games.filter(
      (item) => String(item.leagueId) === String(selectedLeagueForSchedule)
    );
  }, [games, selectedLeagueForSchedule]);

  const createSeason = (name) => {
    if (seasons.length >= 1) {
      alert("Demo limit reached. Create an account to unlock more seasons.");
      return;
    }

    const newSeason = {
      id: Date.now(),
      name: name.trim(),
    };

    setSeasons((prev) => [...prev, newSeason]);
  };

  const deleteSeason = (seasonId) => {
    const seasonLeagues = leagues.filter((item) => item.seasonId === seasonId);
    const seasonLeagueIds = seasonLeagues.map((item) => item.id);

    const seasonTeams = teams.filter((item) => seasonLeagueIds.includes(item.leagueId));
    const seasonTeamIds = seasonTeams.map((item) => item.id);

    setSeasons((prev) => prev.filter((item) => item.id !== seasonId));
    setLeagues((prev) => prev.filter((item) => item.seasonId !== seasonId));
    setTeams((prev) => prev.filter((item) => !seasonLeagueIds.includes(item.leagueId)));
    setPlayers((prev) => prev.filter((item) => !seasonTeamIds.includes(item.teamId)));
    setGames((prev) => prev.filter((item) => !seasonLeagueIds.includes(item.leagueId)));

    if (activeSeasonId === seasonId) {
      setLeftView("seasons");
      setActiveSeasonId(null);
      setActiveLeagueId(null);
      setActiveTeamId(null);
    }

    if (
      selectedLeagueForStandings !== "all" &&
      seasonLeagueIds.includes(Number(selectedLeagueForStandings))
    ) {
      setSelectedLeagueForStandings("all");
    }

    if (
      String(selectedLeagueForPlayers) &&
      seasonLeagueIds.includes(Number(selectedLeagueForPlayers))
    ) {
      setSelectedLeagueForPlayers("");
      setSelectedTeamForPlayers("");
    }

    if (
      selectedLeagueForSchedule !== "all" &&
      seasonLeagueIds.includes(Number(selectedLeagueForSchedule))
    ) {
      setSelectedLeagueForSchedule("all");
    }
  };

  const createLeague = (name) => {
    if (!activeSeasonId) return;

    const newLeague = {
      id: Date.now(),
      name: name.trim(),
      seasonId: activeSeasonId,
    };

    setLeagues((prev) => [...prev, newLeague]);
  };

  const deleteLeague = (leagueId) => {
    const leagueTeams = teams.filter((item) => item.leagueId === leagueId);
    const leagueTeamIds = leagueTeams.map((item) => item.id);

    setLeagues((prev) => prev.filter((item) => item.id !== leagueId));
    setTeams((prev) => prev.filter((item) => item.leagueId !== leagueId));
    setPlayers((prev) => prev.filter((item) => !leagueTeamIds.includes(item.teamId)));
    setGames((prev) => prev.filter((item) => item.leagueId !== leagueId));

    if (activeLeagueId === leagueId) {
      setLeftView("leagues");
      setActiveLeagueId(null);
      setActiveTeamId(null);
    }

    if (String(selectedLeagueForStandings) === String(leagueId)) {
      setSelectedLeagueForStandings("all");
    }

    if (String(selectedLeagueForPlayers) === String(leagueId)) {
      setSelectedLeagueForPlayers("");
      setSelectedTeamForPlayers("");
    }

    if (String(selectedLeagueForSchedule) === String(leagueId)) {
      setSelectedLeagueForSchedule("all");
    }
  };

  const createTeam = (name) => {
    if (!activeLeagueId) return;

    const newTeam = {
      id: Date.now(),
      name: name.trim(),
      leagueId: activeLeagueId,
    };

    setTeams((prev) => [...prev, newTeam]);
  };

  const deleteTeam = (teamId) => {
    setTeams((prev) => prev.filter((item) => item.id !== teamId));
    setPlayers((prev) => prev.filter((item) => item.teamId !== teamId));
    setGames((prev) =>
      prev.filter(
        (item) => item.homeTeamId !== teamId && item.awayTeamId !== teamId
      )
    );

    if (activeTeamId === teamId) {
      setLeftView("teams");
      setActiveTeamId(null);
    }

    if (String(selectedTeamForPlayers) === String(teamId)) {
      setSelectedTeamForPlayers("");
    }
  };

  const createPlayer = (name) => {
    if (!activeTeamId) return;

    const newPlayer = {
      id: Date.now(),
      name: name.trim(),
      teamId: activeTeamId,
    };

    setPlayers((prev) => [...prev, newPlayer]);
  };

  const deletePlayer = (playerId) => {
    setPlayers((prev) => prev.filter((item) => item.id !== playerId));
  };

  const createGame = ({ date, homeTeamId, awayTeamId, location, time }) => {
    if (!activeLeagueId) return;

    const newGame = {
      id: Date.now(),
      leagueId: activeLeagueId,
      date,
      homeTeamId: Number(homeTeamId),
      awayTeamId: Number(awayTeamId),
      location: location.trim(),
      time,
      status: "Scheduled",
      results: "—",
      homeScore: 0,
      awayScore: 0,
    };

    setGames((prev) => [...prev, newGame]);

    if (selectedLeagueForSchedule !== "all" && !selectedLeagueForSchedule) {
      setSelectedLeagueForSchedule(String(activeLeagueId));
    }
  };

  const deleteGame = (gameId) => {
    setGames((prev) => prev.filter((item) => item.id !== gameId));
    setPlayerGameStats((prev) => prev.filter((item) => item.gameId !== gameId));

    if (activeGameId === gameId) {
      setActiveGameId(null);
      setLeftView("teams");
    }

    if (selectedGamePreviewId === gameId) {
      setSelectedGamePreviewId(null);
    }
  };

  const saveFinalScore = ({ gameId, homeScore, awayScore }) => {
    setGames((prev) =>
      prev.map((game) =>
        game.id === gameId
          ? {
            ...game,
            homeScore: Number(homeScore) || 0,
            awayScore: Number(awayScore) || 0,
            status: "Completed",
            results: `${Number(homeScore) || 0} - ${Number(awayScore) || 0}`,
          }
          : game
      )
    );
  };

  const savePlayerStats = ({ gameId, stats }) => {
    setPlayerGameStats((prev) => {
      const filtered = prev.filter((item) => item.gameId !== gameId);

      const normalized = stats.map((item) => ({
        gameId,
        playerId: item.playerId,
        pts: Number(item.pts) || 0,
      }));

      return [...filtered, ...normalized];
    });
  };

  const openSeason = (seasonId) => {
    setActiveSeasonId(seasonId);
    setActiveLeagueId(null);
    setActiveTeamId(null);
    setLeftView("leagues");
  };

  const openLeague = (leagueId) => {
    setActiveLeagueId(leagueId);
    setActiveTeamId(null);
    setLeftView("teams");

    setSelectedLeagueForStandings(String(leagueId));
    setSelectedLeagueForPlayers(String(leagueId));
    setSelectedTeamForPlayers("");
    setSelectedLeagueForSchedule(String(leagueId));
  };

  const openTeam = (teamId) => {
    setActiveTeamId(teamId);
    setLeftView("players");

    const team = teams.find((item) => item.id === teamId);
    if (team) {
      setSelectedLeagueForPlayers(String(team.leagueId));
      setSelectedTeamForPlayers(String(team.id));
    }
  };

  const openGame = (gameId) => {
    setActiveGameId(gameId);
    setLeftView("gameDetail");
  };

  const goToGames = () => {
    setLeftView("teams");
    setActiveGameId(null);
  };

  const goToSeasons = () => {
    setLeftView("seasons");
    setActiveSeasonId(null);
    setActiveLeagueId(null);
    setActiveTeamId(null);
    setActiveGameId(null);
  };

  const goToLeagues = () => {
    setLeftView("leagues");
    setActiveLeagueId(null);
    setActiveTeamId(null);
    setActiveGameId(null);
  };

  const goToTeams = () => {
    setLeftView("teams");
    setActiveTeamId(null);
    setActiveGameId(null);
  };

  const handleResetSandbox = () => {
    setSeasons([]);
    setLeagues([]);
    setTeams([]);
    setPlayers([]);
    setGames([]);
    setPlayerGameStats([]);

    setLeftView("seasons");
    setActiveSeasonId(null);
    setActiveLeagueId(null);
    setActiveTeamId(null);
    setActiveGameId(null);

    setActivePreviewTab("standings");
    setSelectedLeagueForStandings("all");
    setPlayerSearch("");
    setSelectedLeagueForPlayers("");
    setSelectedTeamForPlayers("");
    setSelectedLeagueForSchedule("all");
    setSelectedTeamPreviewId(null);
    setSelectedGamePreviewId(null);
  };

  return (
    <div className="sandbox-demo-page">
      <div className="sandbox-demo-intro">
        <div className="sandbox-demo-intro-text">
          <span className="sandbox-demo-badge">Live Demo</span>
          <h1>Try League Management </h1>
          <p>
            Build a sample season, league, teams, players, games, and stats to see
            how the platform works. This demo is temporary, limited, and not saved. Refreshing the page will reset everything. Create an
            account to save your data and unlock more features.
          </p>
        </div>

        <div className="sandbox-demo-intro-actions">
          <a href="/register" className="sandbox-create-account-btn">
            Create a Free Account
          </a>
          <button
            type="button"
            className="sandbox-reset-btn"
            onClick={handleResetSandbox}
          >
            Reset Demo
          </button>
        </div>
      </div>

      <div className="sandbox-demo-shell">
        <div className="sandbox-demo-panel">
          <div className="sandbox-demo-panel-label">Admin View</div>

          <SandboxLeft
            leftView={leftView}
            isSeasonLimitReached={seasons.length >= 1}
            seasons={seasons}
            leagues={leaguesForActiveSeason}
            teams={teamsForActiveLeague}
            players={playersForActiveTeam}
            allPlayers={players}
            playerGameStats={playerGameStats}
            games={gamesForActiveLeague}
            allTeamsInActiveLeague={teamsForActiveLeague}
            activeSeason={activeSeason}
            activeLeague={activeLeague}
            activeTeam={activeTeam}
            activeGame={activeGame}
            onCreateSeason={createSeason}
            onDeleteSeason={deleteSeason}
            onCreateLeague={createLeague}
            onDeleteLeague={deleteLeague}
            onCreateTeam={createTeam}
            onDeleteTeam={deleteTeam}
            onCreatePlayer={createPlayer}
            onDeletePlayer={deletePlayer}
            onCreateGame={createGame}
            onDeleteGame={deleteGame}
            onSaveFinalScore={saveFinalScore}
            onSavePlayerStats={savePlayerStats}
            onOpenSeason={openSeason}
            onOpenLeague={openLeague}
            onOpenTeam={openTeam}
            onOpenGame={openGame}
            onGoToSeasons={goToSeasons}
            onGoToLeagues={goToLeagues}
            onGoToTeams={goToTeams}
            onGoToGames={goToGames}
          />
        </div>

        <div className="sandbox-demo-panel">
          <div className="sandbox-demo-panel-label">Public View</div>

          <SandboxRight
            activePreviewTab={activePreviewTab}
            setActivePreviewTab={setActivePreviewTab}
            leagues={leagues}
            teams={teams}
            players={players}
            scheduleGames={scheduleGames}
            selectedLeagueForStandings={selectedLeagueForStandings}
            setSelectedLeagueForStandings={setSelectedLeagueForStandings}
            playerSearch={playerSearch}
            setPlayerSearch={setPlayerSearch}
            selectedLeagueForPlayers={selectedLeagueForPlayers}
            setSelectedLeagueForPlayers={setSelectedLeagueForPlayers}
            selectedTeamForPlayers={selectedTeamForPlayers}
            setSelectedTeamForPlayers={setSelectedTeamForPlayers}
            teamsForSelectedPlayerLeague={teamsForSelectedPlayerLeague}
            standingsTeams={standingsTeams}
            selectedLeagueForSchedule={selectedLeagueForSchedule}
            setSelectedLeagueForSchedule={setSelectedLeagueForSchedule}
            selectedTeamPreviewId={selectedTeamPreviewId}
            setSelectedTeamPreviewId={setSelectedTeamPreviewId}
            selectedGamePreviewId={selectedGamePreviewId}
            setSelectedGamePreviewId={setSelectedGamePreviewId}
            playerGameStats={playerGameStats}
          />
        </div>
      </div>
      <div className="sandbox-demo-contact">
        Questions or feedback? Reach out to us at{" "}
        <a href="mailto:hello@sportinghip.com">hello@sportinghip.com</a>
      </div>
    </div>
  );
}

export default SandboxDemo;